import React, { useState } from 'react';
import Select from 'react-select';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import LeaseApiService from '../../services/apiService';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: state.isFocused ? '#000 ' : '#000',
    ':hover': {
      backgroundColor: '#F5F5F5',
      color: '#000 ',
    },
  }),
};

const emptyForm = () => ({
  recordId: 0,
  project: null,
  unitNo: null,
  summary: '',
});

const CustomerSummary = ({ propertyList = [], customerSummaryList, setCustomerSummaryList, readOnly = false }) => {
  const apiService = new LeaseApiService();
  const [formData, setFormData] = useState(emptyForm());
  const [unitList, setUnitList] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const propertyOptions = propertyList?.map((p) => ({ value: p?.value, label: p?.text })) || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = async (name, selected) => {
    if (name === 'project') {
      setUnitList([]);
      setFormData((prev) => ({ ...prev, project: selected, unitNo: null }));
      if (selected?.value != null && selected?.value !== '') {
        const params = new URLSearchParams();
        params.append('DropdownFor', 'CustUnitNoByPropertyNo');
        params.append('CascadValue', selected.value);
        const res = await apiService.getApiParamater('CUSTOMER_CASCAD', `?${params.toString()}`);
        setUnitList(res?.data?.items || []);
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: selected }));
  };

  const resolveProjectFromRow = (row) => {
    if (row.project) return row.project;
    const pNo = row.propertyNo;
    const pName = row.propertyName;
    const hit = (propertyList || []).find((p) => String(p.value) === String(pNo));
    if (hit) return { value: hit.value, label: hit.text };
    if (pNo || pName) return { value: pNo || pName, label: pName || pNo || '' };
    return null;
  };

  const handleAddOrUpdate = () => {
    if (!formData.project) {
      toast.warning('Select property');
      return;
    }
    if (!formData.unitNo) {
      toast.warning('Select unit');
      return;
    }
    if (!formData.summary?.trim()) {
      toast.warning('Enter summary');
      return;
    }
    if (editIndex !== null) {
      const updated = customerSummaryList.map((item, i) => (i === editIndex ? { ...formData } : item));
      setCustomerSummaryList(updated);
      setEditIndex(null);
    } else {
      setCustomerSummaryList((prev) => [...prev, { ...formData, recordId: formData.recordId ?? 0 }]);
    }
    setFormData(emptyForm());
    setUnitList([]);
  };

  const handleEdit = async (index) => {
    const row = customerSummaryList[index];
    const project = resolveProjectFromRow(row);

    let options = [];
    if (project?.value != null && project?.value !== '') {
      const params = new URLSearchParams();
      params.append('DropdownFor', 'CustUnitNoByPropertyNo');
      params.append('CascadValue', project.value);
      const res = await apiService.getApiParamater('CUSTOMER_CASCAD', `?${params.toString()}`);
      options = res?.data?.items || [];
    }
    setUnitList(options);

    let unitSel = row.unitNo;
    if (unitSel?.value != null || unitSel?.label) {
      const uRaw = unitSel.value ?? unitSel.label;
      const hit = options.find((u) => String(u.value) === String(uRaw) || String(u.text) === String(uRaw) || String(u.text) === String(unitSel.label));
      unitSel = hit ? { value: hit.value, label: hit.text } : unitSel;
    } else {
      const legacy = row.unitNoString ?? row.unitNumber;
      if (legacy != null && legacy !== '') {
        const hit = options.find((u) => String(u.value) === String(legacy) || String(u.text) === String(legacy));
        unitSel = hit ? { value: hit.value, label: hit.text } : { value: legacy, label: String(legacy) };
      }
    }

    setFormData({
      recordId: row.recordId ?? 0,
      project,
      unitNo: unitSel ?? null,
      summary: row.summary ?? '',
    });
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    setCustomerSummaryList((prev) => prev.filter((_, i) => i !== index));
    if (editIndex === index) {
      setEditIndex(null);
      setFormData(emptyForm());
      setUnitList([]);
    }
  };

  const unitOptions = unitList?.map((u) => ({ value: u?.value, label: u?.text })) || [];

  return (
    <div className="cost-center-section my-3">
      <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
        {!readOnly && (
          <>
            <div className="form-group grid-contain">
              <div className="d-flex">
                <label className="form-label pt-1 start">Property</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  styles={selectStyles}
                  options={propertyOptions}
                  value={formData.project}
                  onChange={(selected) => handleSelectChange('project', selected)}
                  placeholder="Select property"
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">Unit</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  styles={selectStyles}
                  options={unitOptions}
                  value={formData.unitNo}
                  onChange={(selected) => handleSelectChange('unitNo', selected)}
                  placeholder="Select unit"
                  isDisabled={!formData.project}
                />
              </div>
            </div>

            <div className="mt-3 d-flex justify content-center w-75">
              <label className="form-label d-block mb-2" htmlFor="customer-summary-text">
                Summary
              </label>
              <textarea
                id="customer-summary-text"
                className="p-2 form-control w-100"
                name="summary"
                rows={8}
                style={{ minHeight: '180px', resize: 'vertical' }}
                value={formData.summary}
                onChange={handleInputChange}
                placeholder="Summary"
              />
            </div>

            <div className="my-4">
              <div className="mainbtnSecondary flex-end gap-2 col-2" style={{ cursor: 'pointer' }} onClick={handleAddOrUpdate}>
                <FaPlus color="#fff" className="center mt-1 mx-2" />
                <div className="center">{editIndex !== null ? 'Update Summary' : 'Add Summary'}</div>
              </div>
            </div>
          </>
        )}

        <div className="table-container" style={{ overflowX: 'auto', height: '400px' }}>
          <table className="table table-bordered gridshad">
            <thead className="dynoTable">
              <tr>
                {!readOnly && <th>Action</th>}
                <th>Property</th>
                <th>Unit</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {customerSummaryList.map((data, index) => (
                <tr key={index}>
                  {!readOnly && (
                    <td className="text-center d-flex gap-3">
                      <div className="center mt-2">
                        <FaEdit size={20} style={{ cursor: 'pointer', color: 'black' }} onClick={() => handleEdit(index)} />
                      </div>
                      <div className="center mt-2">
                        <FaTrash size={20} style={{ cursor: 'pointer', color: 'red' }} onClick={() => handleDelete(index)} />
                      </div>
                    </td>
                  )}
                  <td>{data.project?.label ?? data.propertyName ?? '—'}</td>
                  <td>{data.unitNo?.label ?? data.unitNo?.value ?? data.unitNoString ?? '—'}</td>
                  <td style={{ whiteSpace: 'pre-wrap', maxWidth: 480 }}>{data.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerSummary;
